import React from 'react';

import { Banner, Box, Text } from '@betterment/samba-react';

import { PurposeSection } from './PurposeSection';

import { SambaAutoLink } from '../../../features/navigation/SambaAutoLink';
import { ModalSection } from '../../modal-section/ModalSection';
import { ModalSectionRow } from '../../modal-section/ModalSectionRow';
import { formatLegalAccountType, formatTCPAccounts } from '../helpers';

import type { AccountSettingsUrlBuilders, RenderCloseAccountAction, RenderDeletePurposeAction } from '../contentTypes';
import type { ManagedAccount, ManagedPortfolio } from '../data';

type ManagedAccountSettingsProps = {
  account: ManagedAccount;
  currentPath: string;
  urlBuilders: Pick<
    AccountSettingsUrlBuilders,
    | 'legalAccounts'
    | 'editPortfolioStrategy'
    | 'editAllocation'
    | 'editExternallyAdvisedPlan'
    | 'editPurpose'
    | 'accountDetails'
    | 'editPortfolio'
    | 'taxCoordinationChange'
    | 'taxCoordinationSetup'
  >;
  renderDeletePurpose?: RenderDeletePurposeAction;
  renderCloseAccount?: RenderCloseAccountAction;
  clientName?: string;
  showInlineEditLinks?: boolean;
  purposelessCopy?: string;
};

type PortfolioDetails = {
  strategy: {
    name: string;
    editUrl?: string;
  } | null;
  allocation: {
    description: string;
    editUrl?: string;
  } | null;
};

const getPortfolioDetails = (
  portfolio: ManagedPortfolio,
  account: ManagedAccount,
  currentPath: string,
  urlBuilders: ManagedAccountSettingsProps['urlBuilders']
): PortfolioDetails | null => {
  const editStrategyUrl = urlBuilders.editPortfolioStrategy?.({ account, currentPath });
  const editAllocationUrl = urlBuilders.editAllocation?.({ account, currentPath });
  const editExternallyAdvisedPlanUrl = urlBuilders.editExternallyAdvisedPlan?.({ account, currentPath });

  switch (portfolio?.__typename) {
    case 'RiskLevelBasedPortfolio': {
      const { strategy, riskLevel, isStrategySelectionEligible, isAllocationEditable } = portfolio;
      return {
        strategy: strategy?.name
          ? {
              name: strategy.name,
              editUrl: isStrategySelectionEligible ? editStrategyUrl : undefined,
            }
          : null,
        allocation:
          isAllocationEditable && riskLevel?.name
            ? {
                description: riskLevel.name,
                editUrl: editAllocationUrl,
              }
            : null,
      };
    }
    case 'SelfDirectedPortfolio': {
      const { isExternallyAdvised, isStrategySelectionEligible, isAllocationEditable, targetAllocationDescription } =
        portfolio;
      return {
        strategy: portfolio.name
          ? {
              name: portfolio.name,
              editUrl: isStrategySelectionEligible && !isExternallyAdvised ? editStrategyUrl : undefined,
            }
          : null,
        allocation:
          isAllocationEditable && targetAllocationDescription
            ? {
                description: targetAllocationDescription,
                editUrl: isExternallyAdvised ? editExternallyAdvisedPlanUrl : editAllocationUrl,
              }
            : null,
      };
    }
    default:
      return null;
  }
};

export const ManagedAccountSettings: React.FC<ManagedAccountSettingsProps> = ({
  account,
  currentPath,
  urlBuilders,
  renderDeletePurpose,
  renderCloseAccount,
  clientName,
  purposelessCopy,
  showInlineEditLinks = true,
}) => {
  const { isTaxCoordinated, portfolio, subAccounts, autoglide, isTaxCoordinationEligible } = account;
  const primarySubAccount = subAccounts[0];
  const legalAccountType = formatLegalAccountType(
    primarySubAccount?.legalAccount ?? { ownershipType: '', taxationType: '' }
  );
  const tcpAccounts = formatTCPAccounts(subAccounts);
  const hasBeneficiaries = subAccounts.some(subAccount => subAccount.legalAccount.primaryBeneficiaries.length > 0);

  const portfolioDetails = getPortfolioDetails(portfolio, account, currentPath, urlBuilders);
  const editAutoAdjustUrl = urlBuilders.editAllocation?.({ account, currentPath });
  const editTaxCoordinationUrl = isTaxCoordinated
    ? urlBuilders.taxCoordinationChange?.({ account, currentPath })
    : urlBuilders.taxCoordinationSetup?.();

  const closeAccountAction =
    renderCloseAccount &&
    primarySubAccount &&
    renderCloseAccount({
      balance: account.balance,
      purposeName: account.envelope.purpose?.name,
      name: account.name,
      legacyAccountId: primarySubAccount.legacySubAccountId,
    });

  return (
    <Box css={{ display: 'grid', gap: 'space.200' }}>
      <ModalSection header="Account details" cta={urlBuilders.accountDetails}>
        {clientName && (
          <ModalSectionRow>
            <ModalSectionRow.LeadingContent>
              <Text>Client</Text>
            </ModalSectionRow.LeadingContent>
            <ModalSectionRow.TrailingContent>
              <Text>{clientName}</Text>
            </ModalSectionRow.TrailingContent>
          </ModalSectionRow>
        )}
        <ModalSectionRow>
          <ModalSectionRow.LeadingContent>
            <Text>Account type</Text>
          </ModalSectionRow.LeadingContent>
          <ModalSectionRow.TrailingContent>
            <Text>{account.isTaxCoordinated ? 'Tax-coordinated' : legalAccountType}</Text>
          </ModalSectionRow.TrailingContent>
        </ModalSectionRow>
        {!clientName && !hasBeneficiaries && (
          <ModalSectionRow>
            <ModalSectionRow.LeadingContent>
              <Text>Beneficiaries</Text>
            </ModalSectionRow.LeadingContent>
            <ModalSectionRow.TrailingContent>
              {urlBuilders.legalAccounts ? (
                <SambaAutoLink to={urlBuilders.legalAccounts}>Add beneficiary</SambaAutoLink>
              ) : null}
            </ModalSectionRow.TrailingContent>
          </ModalSectionRow>
        )}
        {isTaxCoordinated && (
          <Banner variant="info" css={{ my: 'space.100' }}>
            <Banner.Content>
              <Text color="text.weak">
                Your accounts are organized into a tax coordinated portfolio that includes {tcpAccounts} accounts.
              </Text>
            </Banner.Content>
          </Banner>
        )}
      </ModalSection>
      <ModalSection header="Portfolio" cta={urlBuilders.editPortfolio}>
        {portfolioDetails?.strategy && (
          <ModalSectionRow>
            <ModalSectionRow.LeadingContent>
              <Text>Portfolio strategy</Text>
            </ModalSectionRow.LeadingContent>
            <ModalSectionRow.TrailingContent>
              <Text>{portfolioDetails.strategy.name}</Text>
              {showInlineEditLinks && portfolioDetails.strategy.editUrl && (
                <SambaAutoLink to={portfolioDetails.strategy.editUrl}>Edit</SambaAutoLink>
              )}
            </ModalSectionRow.TrailingContent>
          </ModalSectionRow>
        )}
        {portfolioDetails?.allocation && (
          <ModalSectionRow>
            <ModalSectionRow.LeadingContent>
              <Text>Allocation</Text>
            </ModalSectionRow.LeadingContent>
            <ModalSectionRow.TrailingContent>
              <Text>{portfolioDetails.allocation.description}</Text>
              {showInlineEditLinks && portfolioDetails.allocation.editUrl && (
                <SambaAutoLink to={portfolioDetails.allocation.editUrl}>Edit</SambaAutoLink>
              )}
            </ModalSectionRow.TrailingContent>
          </ModalSectionRow>
        )}
        {autoglide?.isEligible && (
          <ModalSectionRow>
            <ModalSectionRow.LeadingContent>
              <Text>Auto-adjust</Text>
            </ModalSectionRow.LeadingContent>
            <ModalSectionRow.TrailingContent>
              <Text data-testid="auto-adjust-value">{autoglide.isEnabled ? 'On' : 'Off'}</Text>
              {showInlineEditLinks && editAutoAdjustUrl && <SambaAutoLink to={editAutoAdjustUrl}>Edit</SambaAutoLink>}
            </ModalSectionRow.TrailingContent>
          </ModalSectionRow>
        )}
        {isTaxCoordinationEligible && (
          <ModalSectionRow>
            <ModalSectionRow.LeadingContent>
              <Text>Tax Coordination</Text>
            </ModalSectionRow.LeadingContent>
            <ModalSectionRow.TrailingContent>
              <Text data-testid="tax-coordination-value">{isTaxCoordinated ? 'On' : 'Off'}</Text>
              {showInlineEditLinks && editTaxCoordinationUrl && (
                <SambaAutoLink to={editTaxCoordinationUrl}>Edit</SambaAutoLink>
              )}
            </ModalSectionRow.TrailingContent>
          </ModalSectionRow>
        )}
      </ModalSection>
      <PurposeSection
        account={account}
        currentPath={currentPath}
        urlBuilders={{ editPurpose: urlBuilders.editPurpose }}
        renderDeletePurpose={renderDeletePurpose}
        purposelessCopy={purposelessCopy}
      />
      {closeAccountAction}
    </Box>
  );
};
